import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7 } from "styled-components";
import React from "react";
import { Headline } from "~/features/ui/headline";
import { DeepDiveGalleryOverlay } from "~/features/blocks/inventory/deep-dive-gallery/deep-dive-gallery-overlay";
import { useDeepDiveGallery } from "~/features/blocks/inventory/deep-dive-gallery/context";
import { DeepDiveGalleryBlur } from "~/features/blocks/inventory/deep-dive-gallery/deep-dive-gallery-blur";
import { type AutoPlayStatus } from "~/features/blocks/inventory/deep-dive-gallery/use-autoplay";
type StepMediaRenderArgs = {
  setAutoPlayStatus: React.Dispatch<React.SetStateAction<AutoPlayStatus>>;
  setRenderGalleryCursor: React.Dispatch<React.SetStateAction<boolean>>;
  videoRef: React.RefObject<HTMLVideoElement | null>;
};
export type DeepDiveGalleryStepViewProps = {
  hasBlur?: boolean;
  headline?: string | null;
  index: number;
  isActive: boolean;
  isIntro?: boolean;
  renderMedia?: (args: StepMediaRenderArgs) => React.ReactNode;
  text?: React.ReactNode;
};
const StyledTrigger = _styled.button.withConfig({
  componentId: "sc-sf-xcit36-0"
})``;
export function DeepDiveGalleryStepView(props: DeepDiveGalleryStepViewProps) {
  const {
    hasBlur = false,
    headline,
    index,
    isActive,
    isIntro = false,
    renderMedia,
    text
  } = props;
  const [isTextExpanded, setIsTextExpanded] = React.useState(false);
  const {
    sequenceStarted,
    setAutoplayTimeout,
    activeStepIndex,
    setAutoPlayStatus,
    hasStepsOverlay,
    setRenderGalleryCursor
  } = useDeepDiveGallery();
  const videoRef = React.useRef<HTMLVideoElement>(null);
  function onClick() {
    setIsTextExpanded(prev => !prev);
    setAutoPlayStatus("paused");
  }
  React.useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) {
      return;
    }
    videoElement.pause();

    // Only play when the sequence starts to keep in sync with the progress bar.
    if (sequenceStarted && index === activeStepIndex) {
      // Set the video to play from the beginning.
      videoElement.currentTime = 0;
      videoElement.play();

      // Set autoplay timeout in milliseconds.
      setAutoplayTimeout(videoElement.duration * 1000);
    }
  }, [activeStepIndex, sequenceStarted]); // eslint-disable-line react-hooks/exhaustive-deps

  return <_StyledDiv style={{
    opacity: isActive ? 1 : 0,
    pointerEvents: isActive ? "auto" : "none"
  }} $_css={[{
    "position": "absolute",
    "inset": "0",
    "display": "flex",
    "padding": "var(--spacing-md)",
    "transitionProperty": "opacity",
    "transitionDuration": "250ms",
    "transitionTimingFunction": "ease-in-out",
    "@media (min-width: 1024px)": {
      "padding": "var(--spacing-xl)"
    }
  }, isIntro ? {
    "alignItems": "center"
  } : {
    "alignItems": "flex-end"
  }]}>
      <DeepDiveGalleryOverlay show={hasStepsOverlay} />
      <DeepDiveGalleryBlur show={hasBlur || isTextExpanded} />

      {renderMedia?.({
      setAutoPlayStatus,
      setRenderGalleryCursor,
      videoRef
    })}

      <_StyledDiv2 onMouseEnter={() => setRenderGalleryCursor(false)} onMouseLeave={() => setRenderGalleryCursor(true)} role="presentation" $_css2={[{
      "position": "relative",
      "zIndex": "20",
      "marginBottom": "8rem",
      "width": "100%",
      "borderRadius": "4px",
      "@media (min-width: 1024px)": {
        "marginBottom": "0",
        "padding": "0"
      }
    }, !isIntro && {
      "@media (min-width: 1024px)": {
        "maxWidth": "48rem"
      }
    }]}>
        <_StyledDiv3 $_css3={[isIntro ? {
        "textAlign": "center"
      } : {
        "textAlign": "left"
      }]}>
          {headline && <_StyledDiv4>
              <_StyledHeadline text={headline} $_css4={[{
            "color": "var(--white-static)"
          }, isIntro ? {
            "fontSize": "2.2rem",
            "lineHeight": "1.2",
            "fontWeight": "400",
            "@media (min-width: 1024px)": {
              "fontSize": "5.4rem",
              "lineHeight": "1.3",
              "letterSpacing": "0.054000000000000006rem",
              "fontWeight": "400"
            }
          } : {
            "fontSize": "2rem",
            "lineHeight": "1.3",
            "letterSpacing": "0.02rem",
            "fontWeight": "400",
            "@media (min-width: 1024px)": {
              "fontSize": "2.4rem",
              "lineHeight": "1.3",
              "letterSpacing": "0.024rem",
              "fontWeight": "400"
            }
          }]} />
            </_StyledDiv4>}
          {text && <_StyledDiv5>{text}</_StyledDiv5>}
        </_StyledDiv3>
      </_StyledDiv2>
    </_StyledDiv>;
}
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-xcit36-1"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-xcit36-2"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled4("div").withConfig({
  componentId: "sc-sf-xcit36-3"
})`${p => p.$_css3}`;
var _StyledDiv4 = _styled5("div").withConfig({
  componentId: "sc-sf-xcit36-4"
})({
  "flex": "1 1 0%"
});
var _StyledHeadline = _styled6(Headline).withConfig({
  componentId: "sc-sf-xcit36-5"
})`${p => p.$_css4}`;
var _StyledDiv5 = /*#__PURE__*/_styled7("div").withConfig({
  displayName: "deep-dive-gallery-step___StyledDiv5",
  componentId: "sc-dshryb-0"
})({
  "marginTop": "2.4rem",
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "400",
  "color": "var(--white-static)"
});